<template>
  <v-app-bar app flat height="72" class="nav-bar">
    <div class="logo-section">
      <!-- Hamburger toggle (mobile only) -->
      <v-btn
        v-if="isMobile"
        icon
        size="small"
        variant="text"
        :ripple="false"
        class="menu-icon"
        aria-label="Toggle navigation menu"
        @click="drawer = !drawer"
      >
        <v-icon :size="20" aria-hidden="true">mdi-menu</v-icon>
      </v-btn>

      <!-- Ottu Logo (desktop only) -->
      <v-img
        v-if="!isMobile"
        alt="Ottu Logo"
        :src="ottuLogo"
        class="ottu-logo"
        @click="$emit('go-home')"
      ></v-img>

      <!-- Agent Switcher Pill (mobile only, when an agent is selected) -->
      <v-menu
        v-if="isMobile && agentStore.selectedAgent"
        v-model="agentMenuOpen"
        location="bottom start"
        :offset="[30, 0]"
        :close-on-content-click="true"
        :width="pillWidth || undefined"
        :min-width="0"
      >
        <template #activator="{ props: menuProps }">
          <v-btn
            ref="pillBtnRef"
            v-bind="menuProps"
            class="agent-pill"
            color="primary"
            variant="flat"
            :ripple="false"
            aria-label="Switch agent"
          >
            <template v-slot:prepend>
              <v-icon :size="18" color="white">
                {{ agentStore.selectedAgent?.icon || 'mdi-robot-outline' }}
              </v-icon>
            </template>
            <span class="agent-pill-text">{{
              agentStore.selectedAgent?.name.split(' ')[0]
            }}</span>
          </v-btn>
        </template>

        <div class="agent-dropdown">
          <button
            v-for="agent in agentStore.agents"
            :key="agent.id"
            class="agent-dropdown-item"
            :class="{
              'agent-dropdown-item--active':
                agentStore.selectedAgent?.id === agent.id,
            }"
          >
            <!-- do nothing on click for now, just close the menu. Agent switching
            logic is handled in the sidebar for desktop, and we want to keep it
            consistent by just closing the menu on mobile as well.
            @click="$emit('select-agent', agent)" -->
            <v-icon :size="18" color="white">{{ agent.icon }}</v-icon>
            <span class="agent-dropdown-label">{{
              agent.name.split(' ')[0]
            }}</span>
          </button>
        </div>
      </v-menu>
    </div>

    <v-spacer></v-spacer>

    <div class="d-flex align-center nav-right-section">
      <settings-dialog />

      <v-divider v-if="!isMobile" vertical :thickness="2"></v-divider>

      <!-- User Menu -->
      <v-menu v-if="isAuthenticated" location="bottom end">
        <template v-slot:activator="{ props }">
          <!-- Mobile: avatar only -->
          <v-btn
            v-if="isMobile"
            v-bind="props"
            icon
            :ripple="false"
            class="user-avatar-btn"
          >
            <v-img
              :src="avatar"
              :alt="`${userName}'s avatar`"
              class="user-image"
            ></v-img>
          </v-btn>

          <!-- Desktop: avatar + name + chevron -->
          <v-btn v-else v-bind="props" class="user-btn">
            <template v-slot:prepend>
              <v-img
                :src="avatar"
                :alt="`${userName}'s avatar`"
                class="user-image"
              ></v-img>
            </template>
            <span class="user-name">{{ userName }}</span>
            <template v-slot:append>
              <v-icon :size="24" aria-hidden="true">mdi-chevron-down</v-icon>
            </template>
          </v-btn>
        </template>

        <v-list>
          <v-list-item>
            <v-list-item-title>{{ userName }}</v-list-item-title>
            <v-list-item-subtitle>{{ userEmail }}</v-list-item-subtitle>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item @click="$emit('logout')">
            <template v-slot:prepend>
              <v-icon>mdi-logout</v-icon>
            </template>
            <v-list-item-title>Logout</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </v-app-bar>
</template>

<script setup lang="ts">
import avatar from '@/assets/avatar.jpg';
import ottuLogo from '@/assets/ottu-logo.svg';
import { type Agent } from '@/services/agentService';
import { computed, ref, watch } from 'vue';
import SettingsDialog from '../SettingsDialog.vue';
import { useAgentStore } from '@/stores/agentStore';

const agentStore = useAgentStore();

const props = defineProps<{
  drawer: boolean;
  isMobile: boolean;
  userName: string;
  userEmail: string;
  isAuthenticated: boolean;
}>();

const emit = defineEmits<{
  logout: [];
  'go-home': [];
  'update:drawer': [value: boolean];
  'select-agent': [agent: Agent];
}>();

const drawer = computed({
  get: () => props.drawer,
  set: (value) => emit('update:drawer', value),
});

const agentMenuOpen = ref(false);
const pillBtnRef = ref<{ $el: HTMLElement } | null>(null);
const pillWidth = ref(0);

// Measure after DOM paint so offsetWidth is accurate
watch(
  pillBtnRef,
  (newRef) => {
    if (newRef?.$el) {
      pillWidth.value = (newRef.$el as HTMLElement).offsetWidth;
    }
  },
  { flush: 'post' },
);
</script>

<style scoped lang="scss">
.nav-bar {
  padding: 0px 80px 0px 16px !important;
}

@media only screen and (width < $breakpoint-desktop) {
  .nav-bar {
    padding: 0px 16px 0px 16px !important;
  }
}

.logo-section {
  display: inline-flex;
  align-items: center;
  justify-content: start;
  gap: 12px;
}

.menu-icon {
  background-color: rgb(var(--v-theme-background));
  border-radius: 8px;
}

.ottu-logo {
  width: 110px;
  height: 38px;
  cursor: pointer;
}

.agent-pill {
  height: 36px !important;
  min-width: 0 !important;
  max-width: min(180px, 40vw);
  padding: 0 12px !important;
  border-radius: 8px !important;
  overflow: hidden;
}

.agent-pill :deep(.v-btn__content) {
  overflow: hidden;
  max-width: 100%;
}

.agent-pill-text {
  color: #fff;
  font-family: Poppins;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  text-transform: none;
  letter-spacing: normal;
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agent-dropdown {
  background: #1e1e1e;
  border-radius: 12px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.agent-dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 32px;
  border-radius: 8px;
  border: none;
  background: #303030;
  cursor: pointer;
  transition: background 0.2s ease;
}

.agent-dropdown-item--active {
  background: #f77d1a;

  .agent-dropdown-label {
    font-weight: 700;
  }
}

.agent-dropdown-label {
  color: #fff;
  font-family: Poppins;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  letter-spacing: normal;
}

.nav-right-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

@media only screen and (width < $breakpoint-mobile) {
  .nav-right-section {
    gap: 12px;
  }
}

.user-btn {
  height: 40px !important;
  padding: 0 4px;
}

.user-avatar-btn {
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  padding: 0 !important;
  border-radius: 8px !important;
  overflow: hidden;
}

.user-image {
  width: 40px;
  height: 40px;
  border-radius: 8px;
}

.user-name {
  color: #fff;
  font-family: Poppins;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-transform: capitalize;
  letter-spacing: normal;
}
</style>
